<template>
  <div ref="root" class="pp" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="choose(results[active])" @keydown.esc="close">
    <!-- Nombres y correos: sin corrector ni mayúscula automática, que cambiaban
         «marta» por «Marta,» o una dirección por una palabra del diccionario. -->
    <UiInput
      v-model="term"
      :placeholder="placeholder || t('calendar.people.search')"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      :spellcheck="false"
      enterkeyhint="enter"
      role="combobox"
      :aria-expanded="open && results.length > 0"
      aria-autocomplete="list"
      @update:model-value="search"
      @focus="onFocus"
    />
    <!-- En el <body> y colocada contra el campo dentro de lo que se ve
         (useSuggestionPopover): colgada del campo, el diálogo del evento la
         cortaba y con el teclado fuera se abría debajo de las teclas. -->
    <Teleport to="body">
      <ul v-if="open && results.length" ref="list" class="pp__list" role="listbox" :style="floating">
        <!-- mousedown.prevent: el foco se queda en el campo y el teclado no se
             cierra (se invita a varios seguidos). Se elige en el click, que no
             llega tras un arrastre ni cae en lo de debajo al cerrarse. -->
        <li
          v-for="(person, index) in results"
          :key="person.id"
          role="option"
          :aria-selected="index === active"
          class="pp__item"
          :class="{ 'pp__item--on': index === active }"
          @mousedown.prevent
          @click="choose(person)"
        >
          <span class="pp__avatar">{{ initials(person.name) }}</span>
          <span class="pp__text">
            <span class="pp__name">{{ person.name }}</span>
            <span class="pp__mail">{{ person.email }}</span>
          </span>
        </li>
      </ul>
    </Teleport>
    <p v-if="open && !results.length && term.length > 1 && !loading" class="pp__none">{{ t('calendar.people.none') }}</p>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiInput, initials, useSuggestionPopover } from '@general/ui-kit'

import { calendarApi } from '../api'

/**
 * Finds a colleague by name or address in the calendar's own directory — not
 * the users screen, which is for whoever administers accounts.
 */
const props = defineProps({
  placeholder: { type: String, default: '' },
  exclude: { type: Array, default: () => [] },
})
const emit = defineEmits(['pick'])
const { t } = useI18n()

const root = ref(null)
const list = ref(null)
const term = ref('')
const results = ref([])
const open = ref(false)
const active = ref(0)
const loading = ref(false)
let timer = null

// Antes se cerraba con un blur retardado (150 ms) que competía con el toque en
// la fila: en un teléfono lento ganaba el blur y la persona no se añadía.
const { style: floating, reveal } = useSuggestionPopover({
  anchor: root,
  list,
  open: computed(() => open.value && results.value.length > 0),
  onDismiss: () => close(),
  maxHeight: 260,
})

function onFocus() {
  reveal()
  search(term.value)
}

function search(value) {
  clearTimeout(timer)
  timer = setTimeout(async () => {
    loading.value = true
    try {
      const { items } = await calendarApi.people(String(value ?? '').trim())
      results.value = items.filter(person => !props.exclude.includes(person.id)).slice(0, 8)
      active.value = 0
      open.value = true
    } catch {
      results.value = []
    } finally {
      loading.value = false
    }
  }, 250)
}
function move(step) {
  if (!results.value.length) return
  active.value = (active.value + step + results.value.length) % results.value.length
}
function choose(person) {
  if (!person) return
  emit('pick', person)
  term.value = ''
  results.value = []
  open.value = false
}
function close() { open.value = false }

onUnmounted(() => clearTimeout(timer))
</script>

<style scoped>
.pp { position: relative; }
/* Colocada por useSuggestionPopover (fixed, left, top, width, max-height). Por
   encima de un diálogo (UiModal 100) como el resto de listas del kit. */
.pp__list { z-index: 190; box-sizing: border-box; margin: 0; padding: 4px; list-style: none; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius); box-shadow: var(--shadow-lg); max-height: 260px; overflow-y: auto; overscroll-behavior: contain; }
.pp__item { display: flex; align-items: center; gap: 10px; padding: 8px; border-radius: var(--radius-sm); cursor: pointer; }
.pp__item--on, .pp__item:hover { background: var(--color-primary-soft); }
.pp__avatar { width: 30px; height: 30px; border-radius: 50%; background: var(--color-gray-200); display: grid; place-items: center; font-size: var(--text-xs); font-weight: 600; flex: none; }
.pp__text { display: flex; flex-direction: column; min-width: 0; }
.pp__name { font-size: var(--text-sm); font-weight: 500; }
.pp__mail { font-size: var(--text-xs); color: var(--color-text-soft); overflow: hidden; text-overflow: ellipsis; }
.pp__none { font-size: var(--text-xs); color: var(--color-text-mute); margin: 4px 0 0; }
/* Con el dedo, 44 px por fila como mínimo. */
@media (pointer: coarse) {
  .pp__item { min-height: 44px; }
}
</style>
